import { useMemo } from 'react';
import { ApiError } from 'api';

interface UseSuperSearchErrorStateOptions {
  error: unknown;
  hasResults: boolean;
  includeDocuments: boolean;
  includeSavedDestinations: boolean;
  isEnabled: boolean;
  isError: boolean;
}

const useSuperSearchErrorState = ({
  error,
  hasResults,
  includeDocuments,
  includeSavedDestinations,
  isEnabled,
  isError,
}: UseSuperSearchErrorStateOptions) =>
  useMemo(() => {
    const hasSearchError = isEnabled && isError;
    let searchErrorDescription = 'Check your connection and try the search again.';

    if (error instanceof ApiError && error.status === 503) {
      const detail = error.body?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        searchErrorDescription = detail;
      }
    }

    const hasLiveResultFamilies = includeDocuments || includeSavedDestinations;
    const partialErrorDescription = hasLiveResultFamilies
      ? `${searchErrorDescription} Cached firms, companies, pages, and dates are still shown. Documents and saved destinations may be missing.`
      : `${searchErrorDescription} Showing earlier results while the latest search is unavailable.`;

    return {
      hasFatalError: hasSearchError && !hasResults,
      hasPartialError: hasSearchError && hasResults,
      partialErrorDescription,
      searchErrorDescription,
    };
  }, [error, hasResults, includeDocuments, includeSavedDestinations, isEnabled, isError]);

export default useSuperSearchErrorState;
